<template>
  <div class="main_profile_wrapper">
    <div v-if="loading" class="text-center p-5">
      <i class="fa fa-spinner fa-spin fa-3x text-primary"></i>
    </div>
    
    <div v-else-if="subTopicCard" class="details_container">
      <div class="details_header">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <h1 class="title font-weight-bold">{{ subTopicCard.name }}</h1>
            <p class="description text-muted">{{ subTopicCard.description }}</p>
          </div>
          <el-button type="primary" @click="$router.back()">
            <i class="fa fa-arrow-left mr-2"></i> {{ localization('Back') }}
          </el-button>
        </div>
      </div>

      <div class="components_section">
        <h2 class="section_title mb-4">{{ localization('Components') }}</h2>
        
        <div v-if="subTopicCard.components && subTopicCard.components.length > 0" class="components_list">
          <el-card v-for="(component, index) in subTopicCard.components" :key="index" class="component_card mb-4 shadow-sm">
            <div slot="header" class="clearfix">
              <span class="component_title font-weight-bold">{{ component.title }}</span>
            </div>
            <div class="component_body mt-3" v-if="component.body" v-html="component.body"></div>
            
            <div v-if="component.question_cards && component.question_cards.length > 0" class="component_questions mt-4 p-3 bg-light rounded border">
              <h4 class="font-weight-bold mb-3 text-secondary" style="font-size: 1.1rem;">
                <i class="fa fa-question-circle mr-1"></i> {{ localization('Question Cards') }}
              </h4>
              <div v-for="(qCard, qIndex) in component.question_cards" :key="qIndex" class="mb-4 last-mb-0">
                <!-- Display MCQ choices if MCQ -->
                <div v-if="qCard.type === 'mcq' || (qCard.choices && qCard.choices.length > 0)">
                  <div class="d-flex align-items-start mb-2">
                    <span class="badge badge-primary mr-3 mt-1" style="font-size: 0.8rem; border-radius: 50%; width: 22px; height: 22px; display: inline-flex; align-items: center; justify-content: center; background-color: #7f56d9; flex-shrink: 0;">
                      {{ qIndex + 1 }}
                    </span>
                    <div>
                      <span class="text-dark font-weight-500" style="font-size: 1rem;">{{ qCard.question }}</span>
                      <span v-if="qCard.type === 'mcq'" class="badge badge-secondary ml-2 text-white px-2 py-0.5" style="font-size: 0.75rem; background-color: #7f56d9;">MCQ</span>
                    </div>
                  </div>

                  <div class="choices-list pl-5 mt-2">
                    <!-- If the user is a student, let them select the answer without revealing correctness -->
                    <template v-if="isStudent">
                      <div 
                        v-for="(choice, choiceIndex) in qCard.choices" 
                        :key="choiceIndex" 
                        class="d-flex align-items-center mb-2 py-2 px-3 rounded border" 
                        role="button"
                        :style="selectedMCQAnswers[qCard.id || `q_${component.id || index}_${qIndex}`] === (choice.id || choiceIndex) ? 'background-color: #f9f5ff; border-color: #7f56d9; border-left: 4px solid #7f56d9; cursor: pointer;' : 'background-color: #ffffff; border-color: #eaecf0; cursor: pointer;' "
                        @click="$set(selectedMCQAnswers, qCard.id || `q_${component.id || index}_${qIndex}`, choice.id || choiceIndex)"
                      >
                        <i v-if="selectedMCQAnswers[qCard.id || `q_${component.id || index}_${qIndex}`] === (choice.id || choiceIndex)" class="fa fa-dot-circle-o text-primary mr-2" style="font-size: 1.1rem; color: #7f56d9;"></i>
                        <i v-else class="fa fa-circle-o text-muted mr-2" style="font-size: 1.1rem;"></i>
                        <span :class="{'text-primary font-weight-bold': selectedMCQAnswers[qCard.id || `q_${component.id || index}_${qIndex}`] === (choice.id || choiceIndex), 'text-secondary': selectedMCQAnswers[qCard.id || `q_${component.id || index}_${qIndex}`] !== (choice.id || choiceIndex)}">
                          {{ choice.text }}
                        </span>
                      </div>
                    </template>
                    <!-- Otherwise, show standard admin view indicating correctness -->
                    <template v-else>
                      <div 
                        v-for="(choice, choiceIndex) in qCard.choices" 
                        :key="choiceIndex" 
                        class="d-flex align-items-center mb-1 py-1 px-3 rounded" 
                        :style="choice.is_correct ? 'background-color: #ecfdf3; border-left: 3px solid #12b76a;' : 'background-color: #f9fafb; border-left: 3px solid #d0d5dd;'"
                      >
                        <i v-if="choice.is_correct" class="fa fa-check-circle text-success mr-2"></i>
                        <i v-else class="fa fa-circle text-muted mr-2" style="font-size: 0.6rem;"></i>
                        <span :class="{'text-success font-weight-bold': choice.is_correct, 'text-secondary': !choice.is_correct}">
                          {{ choice.text }}
                        </span>
                        <span v-if="choice.is_correct" class="badge badge-success ml-2 px-2 py-0.5" style="font-size: 0.7rem; background-color: #12b76a; color: white;">
                          {{ localization('Correct') }}
                        </span>
                      </div>
                    </template>
                  </div>
                </div>

                <!-- Display Essay Question styled card for Text Question Cards -->
                <div v-else class="essay-question-container">
                  <div class="essay-question-header">
                    <div class="essay-step-label">
                      Step {{ parseQuestionCard(qCard, qIndex).stepNumber }}: <strong>{{ parseQuestionCard(qCard, qIndex).stepName }}</strong>
                    </div>
                    <div class="essay-main-question">
                      {{ parseQuestionCard(qCard, qIndex).mainQuestion }}
                    </div>
                    <div class="essay-help-text" v-if="parseQuestionCard(qCard, qIndex).helpText" v-html="formatHelpText(parseQuestionCard(qCard, qIndex).helpText)">
                    </div>
                  </div>
                  <div class="essay-question-body">
                    <template v-if="isStudent">
                      <el-input
                        type="textarea"
                        :rows="5"
                        :placeholder="localization('Enter your answer here...')"
                        v-model="textQuestionAnswers[qCard.id || `q_${component.id || index}_${qIndex}`]"
                        class="essay-textarea"
                      ></el-input>
                    </template>
                    <template v-else>
                      <div class="essay-answer-preview">
                        {{ qCard.student_answer || qCard.answer || localization('No answer submitted yet.') }}
                      </div>
                    </template>
                  </div>
                </div>
              </div>
            </div>

            <!-- Student answer input for Component 5 and 6 -->
            <div v-if="isStudent && (component.position === 5 || component.position === 6 || index === 4 || index === 5)" class="student-answer-section mt-4 p-3 bg-light rounded border border-primary">
              <h5 class="font-weight-bold mb-2 text-primary">
                <i class="fa fa-edit mr-1"></i> {{ localization('Your Answer') }}
              </h5>
              <el-input
                type="textarea"
                :rows="4"
                :placeholder="localization('Enter your answer here...')"
                v-model="studentAnswers[component.id || `temp_${component.position || index}`]"
              ></el-input>
            </div>
          </el-card>

          <!-- Submit Button for Student Answers -->
          <div v-if="isStudent" class="d-flex justify-content-end mt-4 mb-4">
            <el-button type="success" size="large" @click="submitAnswers" :loading="loading">
              <i class="fa fa-paper-plane mr-2"></i> {{ localization('Submit Answers') }}
            </el-button>
          </div>
        </div>
        
        <div v-else class="text-center p-5 border rounded bg-light">
          <p class="text-muted">{{ localization('No components found for this sub topic.') }}</p>
        </div>
      </div>
    </div>

    <div v-else class="text-center p-5">
      <el-alert
        :title="localization('Error')"
        type="error"
        :description="localization('Sub topic not found or failed to load.')"
        show-icon
      >
      </el-alert>
      <el-button class="mt-4" type="primary" @click="$router.back()">{{ localization('Go Back') }}</el-button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import Swal from "sweetalert2";

export default {
  name: "SubTopicDetails",
  data() {
    return {
      studentAnswers: {},
      selectedMCQAnswers: {},
      textQuestionAnswers: {}
    };
  },
  computed: {
    ...mapGetters("sub_topics_card", [
      "subTopicCard",
      "loading",
    ]),
    isStudent() {
      const user = this.auth();
      const type = user && user.type ? String(user.type).toLowerCase() : '';
      const roles = this.$store.getters["roles/authUserRoles"] || [];
      const hasStudentRole = roles.some(r => r && String(r).toLowerCase() === 'user');
      return type === 'user' || hasStudentRole;
    }
  },
  methods: {
    fetchDetails() {
      const id = this.$route.params.id;
      this.$store.dispatch("sub_topics_card/ShowSubTopicsCard", id).then(response => {
        if (response && response.subTopicsCard && response.subTopicsCard.components) {
          response.subTopicsCard.components.forEach((component, index) => {
            if (component.position === 5 || component.position === 6 || index === 4 || index === 5) {
              const key = component.id || `temp_${component.position || index}`;
              this.$set(this.studentAnswers, key, component.student_answer || component.answer || "");
            }
            if (component.question_cards) {
              component.question_cards.forEach((qCard, qIndex) => {
                const key = qCard.id || `q_${component.id || index}_${qIndex}`;
                const existingVal = qCard.student_answer !== undefined && qCard.student_answer !== null ? qCard.student_answer : (qCard.answer !== undefined && qCard.answer !== null ? qCard.answer : null);
                if (qCard.type === 'text' || !qCard.choices || qCard.choices.length === 0) {
                  this.$set(this.textQuestionAnswers, key, existingVal || "");
                } else {
                  this.$set(this.selectedMCQAnswers, key, existingVal);
                }
              });
            }
          });
        }
      });
    },
    submitAnswers() {
      const components = this.subTopicCard.components || [];
      const answersToStore = [];

      components.forEach((component, compIndex) => {
        if (component.question_cards) {
          component.question_cards.forEach((qCard, qIndex) => {
            const key = qCard.id || `q_${component.id || compIndex}_${qIndex}`;
            
            // Check if it's a text question card
            if (qCard.type === 'text' || !qCard.choices || qCard.choices.length === 0) {
              const answerVal = this.textQuestionAnswers[key];
              if (answerVal !== undefined && answerVal !== null && answerVal !== "") {
                answersToStore.push({
                  question_card_id: qCard.id,
                  answer: answerVal
                });
              }
            } else {
              // It is MCQ
              const selectedVal = this.selectedMCQAnswers[key];
              if (selectedVal !== undefined && selectedVal !== null) {
                answersToStore.push({
                  question_card_id: qCard.id,
                  answer: selectedVal
                });
              }
            }
          });
        }
      });

      if (answersToStore.length === 0) {
        this.$message({
          message: this.localization("Please answer at least one question before submitting."),
          type: "warning"
        });
        return;
      }

      const groupId = this.$route.query && this.$route.query.group_id;
      const payload = { answers: answersToStore };
      if (groupId) {
        payload.group_card_id = groupId;
      }

      this.$store.dispatch("sub_topics_card/BulkStoreQuestionCardAnswers", payload)
        .then(() => {
          Swal.fire({
            title: "",
            icon: "success",
            type: "success",
            text: `${this.localization("Answers submitted successfully!")}`,
            confirmButtonText: `${this.localization("Ok")}`,
            confirmButtonClass: "btn btn-success",
            buttonsStyling: false
          });
        })
        .catch(err => {
          Swal.fire({
            title: "",
            icon: "error",
            type: "error",
            text: `${this.localization("Failed to submit answers. Please try again.")}`,
            confirmButtonText: `${this.localization("Ok")}`,
            confirmButtonClass: "btn btn-danger",
            buttonsStyling: false
          });
        });
    }


  },
  mounted() {
    this.fetchDetails();
  }
};
</script>

<style lang="scss" scoped>
.main_profile_wrapper {
  //background: #f9fafb;
  width: 100%;
  max-width: 100%;
  padding: 10px 30px 60px;
  min-height: 100vh;
}

.details_container {
  max-width: 1000px;
  margin: 0 auto;
}

.details_header {
  border-bottom: 1px solid #eaecf0;
  padding-bottom: 20px;
  
  .title {
    font-size: 1.7rem;
    color: #101828;
    margin-bottom: 8px;
  }
  
  .description {
    font-size: 1.1rem;
    max-width: 800px;
  }
}

.section_title {
  font-size: 1.5rem;
  color: #344054;
  border-left: 4px solid #7f56d9;
  padding-left: 15px;
}

.component_card {
  border-radius: 12px;
  border: 1px solid #eaecf0;
  transition: transform 0.2s;
  
  &:hover {
    transform: translateY(-2px);
  }

  .component_title {
    font-size: 1.25rem;
    color: #101828;
  }
  
  .component_body {
    line-height: 1.6;
    color: #475467;
    
    ::v-deep {
      img {
        max-width: 100%;
        height: auto;
        border-radius: 8px;
      }
      ul, ol {
        padding-left: 20px;
      }
    }
  }
}
.last-mb-0:last-child {
  margin-bottom: 0 !important;
}
</style>
